import React from 'react';
import { HiX } from 'react-icons/hi';
import { Card } from '@/components/ui';
import Select from '@/components/ui/Select';
import { SORT_OPTIONS, SortOption } from '@/constants/notesSorting.constant';
import CreateNoteButton from './CreateNoteButton';
import SearchInput from './SearchInput';
import ViewModeToggle, { ViewMode } from './ViewModeToggle';

interface NotesTopBarProps {
  query?: string;
  onQueryChange?: (q: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  onClickCreate: (type: 'MDX' | 'TXT', encrypted?: boolean) => void;
  defaultEncrypted?: boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  /** Comma-separated active tag filter string, e.g. "work,react" */
  activeTagFilter?: string;
  onClearTagFilter?: () => void;
}

const NotesTopBar: React.FC<NotesTopBarProps> = ({
  query = '',
  onQueryChange,
  sort,
  onSortChange,
  onClickCreate,
  defaultEncrypted = false,
  viewMode,
  onViewModeChange,
  activeTagFilter,
  onClearTagFilter,
}) => {
  const selectedOption = SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0];
  const filterTags = activeTagFilter
    ? activeTagFilter.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  return (
    <Card className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

        <div className="order-first sm:order-last w-full sm:w-auto">
          <CreateNoteButton onCreate={onClickCreate} defaultEncrypted={defaultEncrypted} />
        </div>

        {/* Sort + view mode toggle + search */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">Sort by</span>
            <div className="flex-1 sm:w-[210px]">
              <Select<SortOption>
                isSearchable={false}
                value={selectedOption}
                options={SORT_OPTIONS}
                onChange={(option) => option && onSortChange(option.value)}
              />
            </div>
          </div>
          <div className="w-full md:w-[280px]">
            <SearchInput value={query} onChange={onQueryChange} />
          </div>
        </div>

      </div>

      {/* Active tag filter banner */}
      {filterTags.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Filtered by:
          </span>
          {filterTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 border border-primary-200 dark:border-primary-700"
            >
              {tag}
            </span>
          ))}
          <button
            type="button"
            onClick={onClearTagFilter}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-gray-300 dark:border-gray-600 transition-colors"
            aria-label="Clear tag filter"
          >
            <HiX className="w-3 h-3" />
            Clear filter
          </button>
        </div>
      )}
    </Card>
  );
};

export default NotesTopBar;
